/**
 * Footer.tsx - Chat Input Footer Component
 * Updated: Removed borders, consistent teal background
 */

import { useChatContext, useChatRefs } from '@/contexts/ChatContext';
import { useAssistantsContext } from '@/contexts/AssistantsContext';
import { useCircleContext } from '@/contexts/CircleContext';
import { useFilterContext } from '@/contexts/FilterContext';
import { Box, HStack, IconButton, Skeleton, Stack, Textarea, VStack, Portal, Button, Avatar } from "@chakra-ui/react";
import { TooltipRoot, TooltipTrigger, TooltipContent, TooltipPositioner } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import React, { useRef, useState, useEffect, useLayoutEffect, useCallback } from 'react';
import { LuHandshake, LuSend, LuSmilePlus, LuX } from 'react-icons/lu';
import { getAvatarUrl } from '@/utilities/imageUtils';
import { NSImage } from '@/types/common.types';
import { FilterButton } from '@/ui/components/filters/FilterButton';
import { FilterPanel } from '@/ui/components/filters/FilterPanel';
import { useLocation } from 'react-router';

export const MANUAL_MODE = 0;
export const SCHEDULE_MODE = 1;
export const DISABLED_MODE = 2;

interface FooterProps {
    responding: boolean;
}

interface Assistant {
    prompts: Array<{ position: string; name: string; }>;
    avatar?: NSImage;
    name?: string;
}

export const Footer: React.FC<FooterProps> = ({ responding }) => {
    const { loading, postId, pendingQuestion, setPendingQuestion } = useChatContext();
    const { scrollToBottom } = useChatRefs();
    const { childCircleId } = useCircleContext();
    const { assistantList, onAssistantReply, onAssistantPost, onAssistantEnhance, submitAssistant } = useAssistantsContext();
    const { isFilterOpen, setIsFilterOpen, resetFilters } = useFilterContext();
    const { t, i18n } = useTranslation();

    const [title, setTitle] = useState<string>('');
    const { pathname } = useLocation();
    const [inputDir, setInputDir] = useState<'ltr' | 'rtl'>(() => (i18n.dir() === 'rtl' ? 'rtl' : 'ltr'));
    const [activeButton, setActiveButton] = useState<{ aindex: number; index: number; position: string } | null>(null);
    const [sending, setSending] = useState<boolean>(false);
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const filterButtonRef = useRef<HTMLDivElement>(null);
    const prevPostIdRef = useRef<string>(postId);

    const isRtlText = useCallback((text: string) => {
        const rtlRegex = /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF]/;
        return rtlRegex.test(text);
    }, []);

    useEffect(() => {
        const prev = prevPostIdRef.current;
        const isSwitchingBetweenChats = Boolean(prev) && Boolean(postId) && prev !== postId;
        if (isSwitchingBetweenChats) {
            setTitle('');
            setInputDir(i18n.dir() === 'rtl' ? 'rtl' : 'ltr');
            if (textareaRef.current) textareaRef.current.style.height = 'auto';
            resetFilters();
            if (isFilterOpen) setIsFilterOpen(false);
        }
        prevPostIdRef.current = postId;
    }, [postId, resetFilters, setIsFilterOpen, isFilterOpen, i18n]);

    useEffect(() => {
        if (pathname.includes('/new')) {
            setTitle('');
            if (textareaRef.current) {
                textareaRef.current.style.height = 'auto';
                textareaRef.current.focus();
            }
        }
    }, [pathname]);

    const resizeTextarea = useCallback(() => {
        if (textareaRef.current) {
            textareaRef.current.style.height = 'auto';
            textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 200)}px`;
        }
    }, []);

    // Keep the textarea sized to its content for any title change, including
    // programmatic ones (Enhance result, suggested-question insertion) that
    // don't fire onChange.
    useLayoutEffect(() => {
        resizeTextarea();
    }, [title, resizeTextarea]);

    const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const value = e.target.value;
        setTitle(value);
        setInputDir(value.trim().length === 0 ? (i18n.dir() === 'rtl' ? 'rtl' : 'ltr') : (isRtlText(value) ? 'rtl' : 'ltr'));
        resizeTextarea();
    };

    const handleClearText = () => {
        setTitle('');
        setInputDir(i18n.dir() === 'rtl' ? 'rtl' : 'ltr');
        if (textareaRef.current) {
            textareaRef.current.style.height = 'auto';
            textareaRef.current.focus();
        }
    };

    const handleSend = async (messageOverride?: string) => {
        const messageToSend = (messageOverride || title).trim();
        if (!messageToSend || loading || sending || !childCircleId) return;
        setSending(true);
        scrollToBottom();
        try {
            if (activeButton) {
                if (activeButton.position === "aichat.enhance") {
                    await onAssistantEnhance({ ...activeButton, title: messageToSend }, setTitle);
                    setActiveButton(null);
                    return;
                } else if (activeButton.position === "aichat.assist") {
                    responding ? await onAssistantReply({ ...activeButton, title: messageToSend }) : await onAssistantPost({ ...activeButton, title: messageToSend });
                    setTitle(messageToSend);
                    setActiveButton(null);
                    return;
                }
            }
            if (submitAssistant) {
                responding ? await onAssistantReply({ ...submitAssistant, title: messageToSend }) : await onAssistantPost({ ...submitAssistant, title: messageToSend });
                setTitle(messageToSend);
            }
        } finally {
            setSending(false);
        }
    };

    useEffect(() => {
        if (pendingQuestion && !loading && !sending && childCircleId) {
            setTitle(pendingQuestion);
            setInputDir(isRtlText(pendingQuestion) ? 'rtl' : 'ltr');
            setPendingQuestion(null);
            handleSend(pendingQuestion);
        }
    }, [pendingQuestion]);

    const handleKeyDown = async (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            await handleSend();
        }
    };

    if (!assistantList) {
        return (<HStack gap="5"><Stack flex="1"><Skeleton height="5" /><Skeleton height="5" width="80%" /></Stack></HStack>);
    }
    if (!submitAssistant) return null;

    const placeholder = responding ? t("enter_response") : `${t("enter_prompt")}`;

    const getAssistantButtons = () => {
        const buttons: Array<{ aindex: number; index: number; position: string; name: string; assistant: Assistant; }> = [];
        if (assistantList?.assistants) {
            assistantList.assistants.forEach((assistant: Assistant, aindex: number) => {
                if (assistant.prompts) {
                    assistant.prompts.forEach((prompt, index) => {
                        if (prompt.position === "aichat.assist" || prompt.position === "aichat.enhance") {
                            buttons.push({ aindex, index, position: prompt.position, name: prompt.name, assistant });
                        }
                    });
                }
            });
        }
        return buttons;
    };

    const assistantButtons = getAssistantButtons();

    const handleAssistantButtonClick = (button: { aindex: number; index: number; position: string }) => {
        setActiveButton(activeButton?.aindex === button.aindex && activeButton?.index === button.index ? null : button);
    };

    const getPromptIcon = (position: string) => {
        if (position === "aichat.enhance") return <LuSmilePlus />;
        if (position === "aichat.assist") return <LuHandshake />;
        return null;
    };

    return (
        <VStack width="full" maxWidth={{ base: "100%", md: "calc(1080px - 2rem)" }} position="sticky" bottom="0" marginTop="auto" paddingX={{ base: 2, md: 2 }} paddingTop={2} paddingBottom={{ base: 3, md: 6 }}>
            <Box
                width="full"
                background="var(--cair-surface)"
                borderRadius="var(--cair-radius-lg)"
                boxShadow="var(--cair-shadow-sm)"
                transition="box-shadow 0.2s"
                position="relative"
                padding={{ base: 2, md: 3 }}
                _focusWithin={{ boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.08)" }}
                border="1px solid var(--cair-border)"
            >
                <VStack width="full" gap={3} alignItems="stretch">
                    <HStack width="full" alignItems="flex-start">
                        <Textarea
                            dir={inputDir}
                            textAlign={inputDir === 'rtl' ? 'right' : 'left'}
                            ref={textareaRef}
                            disabled={loading || sending || !childCircleId}
                            variant="outline"
                            width="100%"
                            minHeight="44px"
                            maxHeight="200px"
                            resize="none"
                            rows={1}
                            onChange={handleInputChange}
                            onKeyDown={handleKeyDown}
                            value={title}
                            placeholder={placeholder}
                            padding={{ base: 2, md: 3 }}
                            fontSize={{ base: "16px", md: "md" }}
                            lineHeight="1.5"
                            border="none"
                            background="transparent"
                            _focus={{ outline: "none", border: "none", boxShadow: "none" }}
                            _hover={{ background: "transparent" }}
                        />
                        {title.trim() && (
                            <IconButton
                                aria-label={t("clear_text") || "Clear text"}
                                variant="ghost"
                                size="sm"
                                onClick={handleClearText}
                                disabled={loading || sending}
                                borderRadius="6px"
                                minWidth="32px"
                                height="32px"
                                color="#666666"
                                _hover={{ background: "var(--cair-accent-subtle)", color: "#333333" }}
                            >
                                <LuX size={18} />
                            </IconButton>
                        )}
                    </HStack>

                    <HStack width="full" gap={2} justifyContent="space-between">
                        <HStack gap={2} flexWrap="wrap">
                            <Box ref={filterButtonRef}>
                                <FilterButton disabled={loading} onClick={() => setIsFilterOpen(!isFilterOpen)} />
                            </Box>

                            {assistantButtons.map((button) => {
                                const buttonKey = `${button.aindex}-${button.index}`;
                                const hasAvatar = button.assistant.avatar?.uuid;
                                const avatarUrl = hasAvatar ? getAvatarUrl(button.assistant.avatar) : '';
                                const buttonLabel = button.name || (button.position === "aichat.assist" ? t("assist") : t("enhance"));
                                const isActive = activeButton?.aindex === button.aindex && activeButton?.index === button.index;

                                return (
                                    <TooltipRoot key={buttonKey} openDelay={500} positioning={{ placement: "top" }}>
                                        <TooltipTrigger asChild>
                                            <Button
                                                aria-label={buttonLabel}
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => handleAssistantButtonClick(button)}
                                                disabled={loading}
                                                borderRadius="8px"
                                                height={{ base: "32px", md: "40px" }}
                                                width={{ base: "32px", md: "40px" }}
                                                paddingX={{ base: 2, md: 3 }}
                                                background={isActive ? "var(--cair-accent-subtle)" : "transparent"}
                                                color="#333333"
                                                borderWidth="2px"
                                                borderStyle="solid"
                                                borderColor={isActive ? "var(--cair-accent)" : "transparent"}
                                                _hover={{ background: "var(--cair-accent-subtle)" }}
                                            >
                                                <HStack gap={{ base: 1, md: 1.5 }}>
                                                    <Avatar.Root size="xs" background="transparent" borderRadius="full">
                                                        {hasAvatar ? (
                                                            <>
                                                                <Avatar.Fallback name={button.assistant.name || "A"} />
                                                                <Avatar.Image src={avatarUrl} width="20px" height="20px" />
                                                            </>
                                                        ) : (
                                                            <Avatar.Fallback>
                                                                <Box width="20px" height="20px" display="flex" alignItems="center" justifyContent="center">
                                                                    {getPromptIcon(button.position)}
                                                                </Box>
                                                            </Avatar.Fallback>
                                                        )}
                                                    </Avatar.Root>
                                                </HStack>
                                            </Button>
                                        </TooltipTrigger>
                                        <Portal><TooltipPositioner><TooltipContent>{buttonLabel}</TooltipContent></TooltipPositioner></Portal>
                                    </TooltipRoot>
                                );
                            })}
                        </HStack>

                        <TooltipRoot openDelay={500} positioning={{ placement: "top" }}>
                            <TooltipTrigger asChild>
                                <IconButton
                                    aria-label="Send message"
                                    variant="solid"
                                    size="md"
                                    disabled={loading || sending || !title.trim() || !childCircleId}
                                    onClick={() => handleSend()}
                                    borderRadius="9999px"
                                    width="40px"
                                    height="40px"
                                    background={!loading && !sending && title.trim() ? 'var(--cair-accent)' : '#E5E5E5'}
                                    color={!loading && !sending && title.trim() ? '#FFFFFF' : '#999999'}
                                    _hover={{ background: !loading && !sending && title.trim() ? 'var(--cair-accent-hover)' : '#E5E5E5' }}
                                >
                                    <LuSend />
                                </IconButton>
                            </TooltipTrigger>
                            <Portal><TooltipPositioner><TooltipContent>{t("send") || "Send"}</TooltipContent></TooltipPositioner></Portal>
                        </TooltipRoot>
                    </HStack>
                </VStack>
            </Box>

            <FilterPanel isOpen={isFilterOpen} onClose={() => setIsFilterOpen(false)} anchorRef={filterButtonRef} />
        </VStack>
    );
};